/**
 * A road per job.
 *
 * The wizard used to decide its own shape: the parent chose a mode, then an
 * input method, and eleven step sequences branched off those two answers
 * (`resolveSteps`). Both questions are ours, not theirs — a parent arrives with
 * a job ("a book about my child", "something to put on tonight"), and the job
 * already answers them. So the job names its road: which screens it shows, what
 * it has decided in advance, and what it stops asking once the family's library
 * can answer it.
 *
 * Pure data + pure functions — the wizard reads them, the unit tests pin them,
 * and nothing here touches React or the network.
 */

export type JobId = 'hero' | 'bedtime' | 'together' | 'voice' | 'basedon';

/** Every screen a road may contain, in no particular order. */
export type RoadScreen = 'voice' | 'dictate' | 'record' | 'heroes' | 'storyInput' | 'output' | 'review';

/** How the parent puts the story in. Mirrors the retired `InputMode`. */
export type RoadInput = 'typePick' | 'dictate' | 'record' | 'fromText';

/**
 * What the family already has. The second visit must not re-ask what the first
 * one answered: a family with three heroes and a cloned voice should be
 * CHOOSING, not uploading a photo and recording again.
 */
export interface LibrarySnapshot {
  /** Reusable library heroes. */
  heroCount: number;
  /** Heroes built from a photo — the `hero` job's own question. */
  photoHeroCount: number;
  /** Cloned voices in the family's library. */
  voiceCount: number;
  /** Stories made so far: 0 = first contact. */
  storyCount: number;
}

export const EMPTY_LIBRARY: LibrarySnapshot = {
  heroCount: 0, photoHeroCount: 0, voiceCount: 0, storyCount: 0,
};

/** What the road decides instead of asking (`ContentParameters` defaults). */
export interface RoadParams {
  genre: string;
  ageGroup: string;
  desiredDurationMinutes: number;
  energy: number;
}

export interface JobRoad {
  screens: readonly RoadScreen[];
  input: RoadInput;
  creationMode: 'generate' | 'from-text';
  fromTextMode?: 'based_on';
  /** Narration ordered with the story. */
  wantAudio: boolean;
  /** A book of pages ordered with the story. */
  wantIllustrations: boolean;
  /** …and its pages drawn without a second request. */
  autoGenerateImages: boolean;
  /** Open the photo panel straight away (nothing in the library to reuse). */
  autoOpenPhoto: boolean;
  /** Preselect the newest cloned voice instead of a preset. */
  preferOwnVoice: boolean;
  params: RoadParams;
}

/** The values the first order used to hard-code (`firstStoryPayload`). */
const DEFAULT_PARAMS: RoadParams = {
  genre: 'adventure', ageGroup: '5-7', desiredDurationMinutes: 7, energy: 3,
};

/**
 * The roads, before the library is taken into account.
 *
 * `hero` is the order the landing sells: a BOOK about this child, pages drawn.
 * It carries narration too — the first order shows the product whole
 * (`FIRST.R8`), and for every later visit the output screen is still there to
 * turn it off.
 *
 * `bedtime` is the opposite trade: no book at all, because the parent needs
 * something to put on in the next five minutes.
 *
 * `together` is a conversation by definition, so it opens the interviewer and
 * never shows a text field first.
 *
 * `voice` starts from the voice, not from the plot: picking (or recording) it is
 * the whole reason the parent came.
 *
 * `basedon` is not a door on /create any more — it is how a gallery tale is
 * reused — but the road stays because live links carry `?job=basedon`.
 */
const ROADS: Record<JobId, JobRoad> = {
  hero: {
    screens: ['heroes', 'storyInput', 'review'],
    input: 'typePick',
    creationMode: 'generate',
    wantAudio: true,
    wantIllustrations: true,
    autoGenerateImages: true,
    autoOpenPhoto: true,
    preferOwnVoice: false,
    params: DEFAULT_PARAMS,
  },
  bedtime: {
    screens: ['storyInput', 'review'],
    input: 'typePick',
    creationMode: 'generate',
    wantAudio: true,
    wantIllustrations: false,
    autoGenerateImages: false,
    autoOpenPhoto: false,
    preferOwnVoice: false,
    // Tonight: shorter than the full seven minutes, because it has to end.
    params: { ...DEFAULT_PARAMS, desiredDurationMinutes: 5 },
  },
  together: {
    screens: ['dictate', 'storyInput', 'review'],
    input: 'dictate',
    creationMode: 'generate',
    wantAudio: true,
    wantIllustrations: true,
    autoGenerateImages: true,
    autoOpenPhoto: false,
    preferOwnVoice: false,
    params: DEFAULT_PARAMS,
  },
  voice: {
    screens: ['voice', 'storyInput', 'review'],
    input: 'typePick',
    creationMode: 'generate',
    wantAudio: true,
    wantIllustrations: false,
    autoGenerateImages: false,
    autoOpenPhoto: false,
    preferOwnVoice: true,
    params: DEFAULT_PARAMS,
  },
  basedon: {
    screens: ['storyInput', 'review'],
    input: 'fromText',
    creationMode: 'from-text',
    fromTextMode: 'based_on',
    wantAudio: false,
    wantIllustrations: true,
    autoGenerateImages: true,
    autoOpenPhoto: false,
    preferOwnVoice: false,
    params: DEFAULT_PARAMS,
  },
};

/** The ids `?job=` accepts, plus the older `?intent=` spellings they map onto. */
export const JOB_IDS = Object.keys(ROADS) as JobId[];
export const LEGACY_JOB: Record<string, JobId> = {
  hero: 'hero',
  bedtime: 'bedtime',
  together: 'together',
  voice: 'voice',
  basedon: 'basedon',
  // A book about the child was two tiles and a third entry-less id (gap 2, gap 5).
  illustrated: 'hero',
  personal: 'hero',
  series: 'hero',
};

export function isJobId(v: string | undefined | null): v is JobId {
  return !!v && Object.prototype.hasOwnProperty.call(ROADS, v);
}

/** The job a URL asks for, under either name, or undefined. */
export function jobFromParam(param: string | undefined | null): JobId | undefined {
  if (!param) return undefined;
  const mapped = LEGACY_JOB[param];
  return mapped && isJobId(mapped) ? mapped : undefined;
}

/**
 * The road this family walks for this job.
 *
 * What the library changes:
 * - a photo hero already exists → the photo panel does not fling itself open,
 *   because the natural move is to pick the child who is already there;
 * - nothing in the library at all → the heroes screen is still shown (that is
 *   where the photo goes), but a job that only needs a voice does not pretend
 *   there is something to choose.
 */
export function roadFor(job: JobId, lib: LibrarySnapshot = EMPTY_LIBRARY): JobRoad {
  const base = ROADS[job];
  return {
    ...base,
    autoOpenPhoto: base.autoOpenPhoto && lib.photoHeroCount === 0,
    preferOwnVoice: base.preferOwnVoice && lib.voiceCount > 0,
  };
}

/** The road as plain data, for tests and docs. */
export function roadDefinition(job: JobId): JobRoad {
  return ROADS[job];
}
